import type { ReactNode } from "react";
import { Dialog as AriaDialog, Heading as AriaHeading, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { X } from "@untitledui/icons";
import { cx } from "@/utils/cx";

/**
 * Right-anchored slide-over (drawer) postavený nad react-aria-components
 * (ModalOverlay/Modal/Dialog) — stejný stack jako `application/modals/modal.tsx`.
 *
 * react-aria dává zdarma: focus trap + autofocus prvního focusable prvku,
 * Escape dismiss, restore focus po zavření, aria-modal a scroll lock.
 * `aria-labelledby` je navázán automaticky přes <Heading slot="title">.
 *
 * API je identické s legacy `components/ui/slide-over.tsx`, takže call-sites
 * se nemění (open/title/onClose/children/footer/size).
 */

export type SlideOverSize = "default" | "wide" | "xwide" | "full";

/** Plné Tailwind třídy (žádná dynamická konkatenace — JIT je musí vidět celé). */
const SIZE_WIDTH: Record<SlideOverSize, string> = {
    default: "w-[min(420px,100vw)]",
    wide: "w-[min(720px,100vw)]",
    xwide: "w-[min(1600px,100vw)]",
    full: "w-[95vw]",
};

export interface SlideOverProps {
    open: boolean;
    title: string;
    onClose: () => void;
    children: ReactNode;
    footer?: ReactNode;
    /** Šířka panelu — default 420px, wide 720px, xwide 1600px (širší tabulky), full 95vw. */
    size?: SlideOverSize;
}

export function SlideOver({ open, title, onClose, children, footer, size = "default" }: SlideOverProps) {
    return (
        <AriaModalOverlay
            isOpen={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) onClose();
            }}
            isDismissable
            className={(state) =>
                cx(
                    "fixed inset-0 z-50 flex w-full justify-end bg-overlay/70 outline-hidden backdrop-blur-[6px]",
                    state.isEntering && "duration-300 ease-out animate-in fade-in",
                    state.isExiting && "duration-200 ease-in animate-out fade-out",
                )
            }
        >
            <AriaModal
                className={(state) =>
                    cx(
                        SIZE_WIDTH[size],
                        "flex h-full max-h-full flex-col bg-primary shadow-xl outline-hidden",
                        state.isEntering && "duration-300 ease-out animate-in slide-in-from-right",
                        state.isExiting && "duration-200 ease-in animate-out slide-out-to-right",
                    )
                }
            >
                <AriaDialog className="flex h-full flex-col outline-hidden">
                    <header className="flex items-center justify-between gap-4 border-b border-secondary px-5 py-4">
                        <AriaHeading slot="title" className="m-0 text-lg font-semibold text-primary">
                            {title}
                        </AriaHeading>
                        <button
                            type="button"
                            aria-label="Zavřít panel"
                            onClick={onClose}
                            className="inline-flex min-h-touch min-w-touch cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-tertiary hover:bg-secondary hover:text-primary focus-visible:outline-2 focus-visible:outline-brand"
                        >
                            <X className="size-5 shrink-0" aria-hidden="true" />
                        </button>
                    </header>
                    <div className="flex-1 overflow-y-auto p-5 text-primary">{children}</div>
                    {footer && <footer className="flex justify-end gap-2 border-t border-secondary px-5 py-4">{footer}</footer>}
                </AriaDialog>
            </AriaModal>
        </AriaModalOverlay>
    );
}
